import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Button,
  Box,
  Card,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import ReactApexChart from 'react-apexcharts';
import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import { useNavigate } from 'react-router-dom';
import centersService from 'services/centersService';
import { getCurrentUser } from 'services/authService';
import reportsService from 'services/reportsService';
import { USER_ROLES } from 'constants/roles';
import { formatCurrency, formatDate, formatNumber, formatPaymentMethod, formatStatus } from 'utils/format';
import {
  MdAttachMoney,
  MdBarChart,
  MdCheckCircle,
  MdGroups,
  MdPayment,
  MdPendingActions,
  MdSchool,
  MdWarning,
} from 'react-icons/md';

const emptySummary = {
  totals: {
    total_students: 0,
    students_with_tuitions: 0,
    students_without_tuitions: 0,
    total_tuitions: 0,
    total_tuition_amount: 0,
    total_paid: 0,
    total_pending: 0,
    total_overdue: 0,
    collection_rate: 0,
  },
  students_by_center: {},
  students_by_region: {},
  students_by_field: {},
  tuition_by_status: {},
  tuition_amount_by_status: {},
  tuition_amount_by_phase: {},
  payments_by_status: {},
  revenue_by_payment_method: {},
  monthly_students: [],
  monthly_revenue: [],
  recent_students: [],
  recent_payments: [],
};

function objectEntries(data = {}) {
  return Object.entries(data || {}).filter(([, value]) => Number(value) > 0);
}

function sortEntries(entries = []) {
  return [...entries].sort((a, b) => Number(b[1]) - Number(a[1]));
}

function compactEntries(entries = [], maxItems = 6, otherLabel = 'Other') {
  const sorted = sortEntries(entries);
  if (sorted.length <= maxItems) return sorted;

  const visible = sorted.slice(0, maxItems);
  const otherTotal = sorted
    .slice(maxItems)
    .reduce((sum, [, value]) => sum + Number(value || 0), 0);

  return otherTotal > 0 ? [...visible, [otherLabel, otherTotal]] : visible;
}

function topEntries(entries = [], maxItems = 10) {
  return sortEntries(entries).slice(0, maxItems);
}

function labelize(value) {
  return String(value || 'Unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function ChartCard({ icon, title, children }) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const brandColor = useColorModeValue('brand.500', 'brand.400');
  const cardBg = useColorModeValue('white', 'navy.800');

  return (
    <Card p='24px' bg={cardBg} w='100%'>
      <Flex align='center' gap='10px' mb='18px'>
        <Icon as={icon} color={brandColor} boxSize='22px' />
        <Text color={textColor} fontSize='lg' fontWeight='700'>
          {title}
        </Text>
      </Flex>
      {children}
    </Card>
  );
}

function EmptyChart({ children }) {
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  return (
    <Flex minH='260px' align='center' justify='center'>
      <Text color={textColorSecondary}>{children}</Text>
    </Flex>
  );
}

function StudentSharedFilters({
  studentFilters,
  onStudentChange,
  loadingCenters,
  centers,
  isCenterScoped,
  centerName,
  inputBg,
  textColor,
}) {
  const centerOptions = centers.length > 0 ? centers : centerName ? [{ id: centerName, name: centerName }] : [];

  return (
    <>
      <FormControl>
        <FormLabel color={textColor} fontSize='sm' mb='4px'>
          Center
        </FormLabel>
        <Select
          value={studentFilters.center}
          onChange={(e) => onStudentChange('center', e.target.value)}
          placeholder={loadingCenters ? 'Loading centers...' : 'Select center'}
          isDisabled={loadingCenters || isCenterScoped}
          bg={inputBg}
          color={textColor}
        >
          {centerOptions.map((center) => (
            <option key={center.id} value={center.name || ''}>
              {center.name || 'Unknown Center'}
            </option>
          ))}
        </Select>
      </FormControl>

      <FormControl>
        <FormLabel color={textColor} fontSize='sm' mb='4px'>
          Field
        </FormLabel>
        <Select
          value={studentFilters.field}
          onChange={(e) => onStudentChange('field', e.target.value)}
          placeholder='Select field'
          bg={inputBg}
          color={textColor}
        >
          <option value='engineering'>engineering</option>
          <option value='medical'>medical</option>
          <option value='other'>other</option>
        </Select>
      </FormControl>
    </>
  );
}

function TuitionSharedFilters({
  tuitionFilters,
  onTuitionChange,
  loadingCenters,
  centers,
  isCenterScoped,
  centerName,
  inputBg,
  textColor,
}) {
  const centerOptions = centers.length > 0 ? centers : centerName ? [{ id: centerName, name: centerName }] : [];

  return (
    <>
      <FormControl>
        <FormLabel color={textColor} fontSize='sm' mb='4px'>
          Center
        </FormLabel>
        <Select
          value={tuitionFilters.center}
          onChange={(e) => onTuitionChange('center', e.target.value)}
          placeholder={loadingCenters ? 'Loading centers...' : 'Select Center'}
          isDisabled={loadingCenters || isCenterScoped}
          bg={inputBg}
          color={textColor}
        >
          {centerOptions.map((center) => (
            <option key={center.id} value={center.name || ''}>
              {center.name || 'Unknown Center'}
            </option>
          ))}
        </Select>
      </FormControl>

      <FormControl>
        <FormLabel color={textColor} fontSize='sm' mb='4px'>
          Field
        </FormLabel>
        <Select
          value={tuitionFilters.field}
          onChange={(e) => onTuitionChange('field', e.target.value)}
          placeholder='Select Field'
          bg={inputBg}
          color={textColor}
        >
          <option value='engineering'>engineering</option>
          <option value='medical'>medical</option>
          <option value='other'>other</option>
        </Select>
      </FormControl>

      <FormControl>
        <FormLabel color={textColor} fontSize='sm' mb='4px'>
          Phase
        </FormLabel>
        <Select
          value={tuitionFilters.phase}
          onChange={(e) => onTuitionChange('phase', e.target.value)}
          placeholder='Select Phase'
          bg={inputBg}
          color={textColor}
        >
          <option value='First Installment'>First Installment</option>
          <option value='Second Installment'>Second Installment</option>
          <option value='Text Book & Work Book'>Text Book & Work Book</option>
        </Select>
      </FormControl>

      <FormControl>
        <FormLabel color={textColor} fontSize='sm' mb='4px'>
          Status Filter
        </FormLabel>
        <Select
          value={tuitionFilters.status_filter}
          onChange={(e) => onTuitionChange('status_filter', e.target.value)}
          placeholder='Select Status'
          bg={inputBg}
          color={textColor}
        >
          <option value='pending'>pending</option>
          <option value='paid'>paid</option>
          <option value='validated'>validated</option>
          <option value='overdue'>overdue</option>
          <option value='cancelled'>cancelled</option>
        </Select>
      </FormControl>

      <FormControl>
        <FormLabel color={textColor} fontSize='sm' mb='4px'>
          Prepa Session
        </FormLabel>
        <Input
          value={tuitionFilters.prepa_session}
          onChange={(e) => onTuitionChange('prepa_session', e.target.value)}
          placeholder='Preparation session (e.g. 2024-2025)'
          bg={inputBg}
          color={textColor}
        />
      </FormControl>
    </>
  );
}

export default function StudentReports() {
  const navigate = useNavigate();
  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const primaryColor = useColorModeValue('brand.500', 'brand.400');
  const inputBg = useColorModeValue('white', 'whiteAlpha.100');
  const panelBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const panelBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const brandColor = useColorModeValue('brand.500', 'white');
  const chartTextColor = useColorModeValue('#2D3748', '#FFFFFF');
  const gridColor = useColorModeValue('#E2E8F0', '#2D3748');
  const rowHoverBg = useColorModeValue('secondaryGray.50', 'whiteAlpha.50');
  const currentUser = getCurrentUser();
  const isCenterScoped = currentUser?.role === USER_ROLES.CENTER_ADMIN;
  const centerName = currentUser?.center || '';

  const [studentFilters, setStudentFilters] = useState({
    center: isCenterScoped ? centerName : '',
    field: '',
    skip: 0,
    limit: 100,
  });
  const [tuitionFilters, setTuitionFilters] = useState({
    center: isCenterScoped ? centerName : '',
    field: '',
    phase: '',
    status_filter: '',
    prepa_session: '',
    skip: 0,
    limit: 100,
  });
  const [centers, setCenters] = useState([]);
  const [loadingCenters, setLoadingCenters] = useState(false);
  const [classListPdfLimit, setClassListPdfLimit] = useState(1000);
  /** Max rows for tuition PDF export (distinct from class list PDF limit). */
  const [tuitionPdfLimit, setTuitionPdfLimit] = useState(1000);
  const [classListPdfDownloading, setClassListPdfDownloading] = useState(false);
  const [tuitionPdfDownloading, setTuitionPdfDownloading] = useState(false);
  const [summary, setSummary] = useState(emptySummary);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);

  useEffect(() => {
    if (isCenterScoped) {
      setCenters(centerName ? [{ id: centerName, name: centerName }] : []);
      return;
    }

    const fetchCenters = async () => {
      setLoadingCenters(true);
      try {
        const result = await centersService.getCenters();
        if (result.success && Array.isArray(result.data)) {
          setCenters(result.data);
        } else {
          setCenters([]);
        }
      } catch (error) {
        console.error('Failed to load centers:', error);
        setCenters([]);
      } finally {
        setLoadingCenters(false);
      }
    };

    fetchCenters();
  }, [centerName, isCenterScoped]);

  useEffect(() => {
    const fetchSummary = async () => {
      setSummaryLoading(true);
      setSummaryError(null);
      try {
        const result = await reportsService.getStudentTuitionSummary();
        if (result.success && result.data) {
          setSummary(result.data);
        } else {
          setSummaryError(result.error || 'Failed to load report statistics.');
        }
      } catch (error) {
        console.error('Student tuition summary error:', error);
        setSummaryError('Failed to load report statistics.');
      } finally {
        setSummaryLoading(false);
      }
    };

    fetchSummary();
  }, []);

  const handleStudentFilterChange = (key, value) => {
    if (key === 'skip') {
      const skipValue = Number.isNaN(Number(value)) ? 0 : Math.max(0, Number(value));
      setStudentFilters((prev) => ({ ...prev, skip: skipValue }));
      return;
    }

    if (key === 'limit') {
      const parsedLimit = Number.isNaN(Number(value)) ? 100 : Number(value);
      const limitValue = Math.min(1000, Math.max(1, parsedLimit));
      setStudentFilters((prev) => ({ ...prev, limit: limitValue }));
      return;
    }

    setStudentFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleTuitionFilterChange = (key, value) => {
    if (key === 'skip') {
      const skipValue = Number.isNaN(Number(value)) ? 0 : Math.max(0, Number(value));
      setTuitionFilters((prev) => ({ ...prev, skip: skipValue }));
      return;
    }

    if (key === 'limit') {
      const parsedLimit = Number.isNaN(Number(value)) ? 100 : Number(value);
      const limitValue = Math.min(1000, Math.max(1, parsedLimit));
      setTuitionFilters((prev) => ({ ...prev, limit: limitValue }));
      return;
    }

    setTuitionFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleGenerateList = () => {
    const queryParams = new URLSearchParams();

    if (studentFilters.center) queryParams.set('center', studentFilters.center);
    if (studentFilters.field) queryParams.set('field', studentFilters.field);
    queryParams.set('skip', String(studentFilters.skip));
    queryParams.set('limit', String(studentFilters.limit));

    navigate(`/admin/student-reports/class-list?${queryParams.toString()}`);
  };

  const handleGenerateTuitionList = () => {
    const queryParams = new URLSearchParams();

    if (tuitionFilters.center) queryParams.set('center', tuitionFilters.center);
    if (tuitionFilters.field) queryParams.set('field', tuitionFilters.field);
    if (tuitionFilters.phase) queryParams.set('phase', tuitionFilters.phase);
    if (tuitionFilters.status_filter) queryParams.set('status_filter', tuitionFilters.status_filter);
    if (tuitionFilters.prepa_session) queryParams.set('prepa_session', tuitionFilters.prepa_session);
    queryParams.set('skip', String(tuitionFilters.skip));
    queryParams.set('limit', String(tuitionFilters.limit));

    navigate(`/admin/student-reports/tuition-payments?${queryParams.toString()}`);
  };

  const handleClassListPdfLimitChange = (raw) => {
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) {
      setClassListPdfLimit(1000);
      return;
    }
    setClassListPdfLimit(Math.min(10000, Math.max(1, Math.floor(parsed))));
  };

  const handleTuitionPdfLimitChange = (raw) => {
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) {
      setTuitionPdfLimit(1000);
      return;
    }
    setTuitionPdfLimit(Math.min(10000, Math.max(1, Math.floor(parsed))));
  };

  const pdfToast = (title, ok, detail) =>
    toast({
      title,
      description: detail,
      status: ok ? 'success' : 'error',
      duration: ok ? 4000 : 6000,
      isClosable: true,
    });

  const handleDownloadClassListPdf = async () => {
    setClassListPdfDownloading(true);
    try {
      const result = await reportsService.downloadClassListPdf({
        center: studentFilters.center || undefined,
        field: studentFilters.field || undefined,
        skip: studentFilters.skip,
        limit: classListPdfLimit,
      });

      if (result.success) {
        pdfToast(
          'Class list PDF',
          true,
          result.filename ? `Saving as ${result.filename}` : 'Download started.',
        );
      } else {
        pdfToast('Class list PDF failed', false, result.error || 'Something went wrong.');
      }
    } finally {
      setClassListPdfDownloading(false);
    }
  };

  const handleDownloadTuitionPdf = async () => {
    setTuitionPdfDownloading(true);
    try {
      const result = await reportsService.downloadTuitionPaymentsPdf({
        center: tuitionFilters.center || undefined,
        field: tuitionFilters.field || undefined,
        phase: tuitionFilters.phase || undefined,
        status_filter: tuitionFilters.status_filter || undefined,
        prepa_session: (tuitionFilters.prepa_session || '').trim() || undefined,
        skip: tuitionFilters.skip,
        limit: tuitionPdfLimit,
      });

      if (result.success) {
        pdfToast(
          'Tuition PDF',
          true,
          result.filename ? `Saving as ${result.filename}` : 'Download started.',
        );
      } else {
        pdfToast('Tuition PDF failed', false, result.error || 'Something went wrong.');
      }
    } finally {
      setTuitionPdfDownloading(false);
    }
  };

  const totals = summary?.totals || emptySummary.totals;
  const studentFieldEntries = compactEntries(objectEntries(summary.students_by_field), 6, 'Other Fields');
  const centerEntries = topEntries(objectEntries(summary.students_by_center), 10);
  const tuitionStatusEntries = objectEntries(summary.tuition_by_status);
  const phaseAmountEntries = topEntries(objectEntries(summary.tuition_amount_by_phase), 10);
  const paymentMethodEntries = compactEntries(objectEntries(summary.revenue_by_payment_method), 6, 'Other Methods');
  const monthlyStudents = summary.monthly_students || [];
  const monthlyRevenue = summary.monthly_revenue || [];
  const recentStudents = summary.recent_students || [];
  const recentPayments = summary.recent_payments || [];

  const chartBase = {
    chart: { toolbar: { show: false }, foreColor: chartTextColor },
    grid: { borderColor: gridColor },
    dataLabels: { enabled: false },
    legend: { position: 'bottom', labels: { colors: chartTextColor } },
  };

  const donutOptions = (labels, colors) => ({
    ...chartBase,
    labels,
    colors,
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '12px',
      labels: { colors: chartTextColor },
      itemMargin: { horizontal: 8, vertical: 4 },
    },
    stroke: { width: 0 },
    dataLabels: { enabled: true, formatter: (value) => `${value.toFixed(0)}%` },
  });

  const barOptions = (categories, colors = ['#2563EB'], horizontal = false) => ({
    ...chartBase,
    colors,
    xaxis: {
      categories,
      labels: { style: { colors: chartTextColor } },
    },
    yaxis: {
      labels: {
        maxWidth: horizontal ? 180 : undefined,
        style: { colors: chartTextColor, fontSize: '12px' },
      },
    },
    plotOptions: {
      bar: {
        horizontal,
        borderRadius: 5,
        columnWidth: horizontal ? undefined : '48%',
      },
    },
  });

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Box mb='24px'>
        <Text color={textColor} fontSize='2xl' fontWeight='700'>
          Reports
        </Text>
        <Text color={textColorSecondary} fontSize='sm' mt='4px'>
          Student enrollment, tuition collection, payments, and export reports in one place.
        </Text>
      </Box>

      {summaryLoading ? (
        <Flex justify='center' align='center' minH='260px' mb='24px'>
          <Spinner size='xl' color='brand.500' />
        </Flex>
      ) : summaryError ? (
        <Alert status='error' borderRadius='12px' mb='24px'>
          <AlertIcon />
          {summaryError}
        </Alert>
      ) : (
        <VStack spacing='20px' align='stretch' mb='32px'>
          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} gap='20px'>
            <MiniStatistics
              startContent={
                <IconBox w='56px' h='56px' bg={boxBg} icon={<Icon w='32px' h='32px' as={MdSchool} color={brandColor} />} />
              }
              name='Total Students'
              value={formatNumber(totals.total_students)}
            />
            <MiniStatistics
              startContent={
                <IconBox w='56px' h='56px' bg={boxBg} icon={<Icon w='32px' h='32px' as={MdPayment} color='cyan.500' />} />
              }
              name='Total Tuitions'
              value={formatNumber(totals.total_tuitions)}
            />
            <MiniStatistics
              startContent={
                <IconBox w='56px' h='56px' bg={boxBg} icon={<Icon w='32px' h='32px' as={MdAttachMoney} color='green.500' />} />
              }
              name='Tuition Value'
              value={formatCurrency(totals.total_tuition_amount)}
            />
            <MiniStatistics
              startContent={
                <IconBox w='56px' h='56px' bg={boxBg} icon={<Icon w='32px' h='32px' as={MdCheckCircle} color='green.500' />} />
              }
              name='Collection Rate'
              value={`${totals.collection_rate || 0}%`}
            />
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} gap='20px'>
            <MiniStatistics
              startContent={
                <IconBox w='56px' h='56px' bg={boxBg} icon={<Icon w='32px' h='32px' as={MdCheckCircle} color='green.500' />} />
              }
              name='Total Paid'
              value={formatCurrency(totals.total_paid)}
            />
            <MiniStatistics
              startContent={
                <IconBox w='56px' h='56px' bg={boxBg} icon={<Icon w='32px' h='32px' as={MdPendingActions} color='orange.500' />} />
              }
              name='Pending Balance'
              value={formatCurrency(totals.total_pending)}
            />
            <MiniStatistics
              startContent={
                <IconBox w='56px' h='56px' bg={boxBg} icon={<Icon w='32px' h='32px' as={MdWarning} color='red.500' />} />
              }
              name='Overdue Balance'
              value={formatCurrency(totals.total_overdue)}
            />
            <MiniStatistics
              startContent={
                <IconBox w='56px' h='56px' bg={boxBg} icon={<Icon w='32px' h='32px' as={MdGroups} color='purple.500' />} />
              }
              name='Without Tuition'
              value={formatNumber(totals.students_without_tuitions)}
            />
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, xl: 2 }} gap='20px'>
            <ChartCard icon={MdBarChart} title='Students by Field'>
              {studentFieldEntries.length ? (
                <ReactApexChart
                  options={donutOptions(studentFieldEntries.map(([label]) => labelize(label)), ['#2563EB', '#16A34A', '#F59E0B', '#0891B2'])}
                  series={studentFieldEntries.map(([, value]) => value)}
                  type='donut'
                  width='100%'
                  height={300}
                />
              ) : (
                <EmptyChart>No student field data yet.</EmptyChart>
              )}
            </ChartCard>

            <ChartCard icon={MdPendingActions} title='Tuition Status'>
              {tuitionStatusEntries.length ? (
                <ReactApexChart
                  options={donutOptions(tuitionStatusEntries.map(([label]) => labelize(label)), ['#16A34A', '#F59E0B', '#DC2626', '#2563EB', '#6B7280'])}
                  series={tuitionStatusEntries.map(([, value]) => value)}
                  type='donut'
                  width='100%'
                  height={300}
                />
              ) : (
                <EmptyChart>No tuition status data yet.</EmptyChart>
              )}
            </ChartCard>
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, xl: 2 }} gap='20px'>
            <ChartCard icon={MdAttachMoney} title='Revenue by Month'>
              {monthlyRevenue.length ? (
                <ReactApexChart
                  options={barOptions(monthlyRevenue.map((item) => item.month), ['#16A34A'])}
                  series={[{ name: 'Revenue', data: monthlyRevenue.map((item) => item.amount || 0) }]}
                  type='bar'
                  width='100%'
                  height={300}
                />
              ) : (
                <EmptyChart>No validated payment revenue yet.</EmptyChart>
              )}
            </ChartCard>

            <ChartCard icon={MdSchool} title='Applications by Month'>
              {monthlyStudents.length ? (
                <ReactApexChart
                  options={barOptions(monthlyStudents.map((item) => item.month), ['#2563EB'])}
                  series={[{ name: 'Students', data: monthlyStudents.map((item) => item.count || 0) }]}
                  type='bar'
                  width='100%'
                  height={300}
                />
              ) : (
                <EmptyChart>No monthly application data yet.</EmptyChart>
              )}
            </ChartCard>
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, xl: 2 }} gap='20px'>
            <ChartCard icon={MdPayment} title='Tuition Amount by Phase'>
              {phaseAmountEntries.length ? (
                <ReactApexChart
                  options={barOptions(phaseAmountEntries.map(([label]) => labelize(label)), ['#0891B2'], true)}
                  series={[{ name: 'Amount', data: phaseAmountEntries.map(([, value]) => value) }]}
                  type='bar'
                  width='100%'
                  height={320}
                />
              ) : (
                <EmptyChart>No tuition phase data yet.</EmptyChart>
              )}
            </ChartCard>

            <ChartCard icon={MdAttachMoney} title='Revenue by Payment Method'>
              {paymentMethodEntries.length ? (
                <ReactApexChart
                  options={donutOptions(paymentMethodEntries.map(([label]) => formatPaymentMethod(label)), ['#2563EB', '#16A34A', '#F59E0B', '#0891B2'])}
                  series={paymentMethodEntries.map(([, value]) => value)}
                  type='donut'
                  width='100%'
                  height={320}
                />
              ) : (
                <EmptyChart>No payment method data yet.</EmptyChart>
              )}
            </ChartCard>
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, xl: 2 }} gap='20px'>
            <ChartCard icon={MdGroups} title='Students by Center'>
              {centerEntries.length ? (
                <ReactApexChart
                  options={barOptions(centerEntries.map(([label]) => label), ['#2563EB'], true)}
                  series={[{ name: 'Students', data: centerEntries.map(([, value]) => value) }]}
                  type='bar'
                  width='100%'
                  height={340}
                />
              ) : (
                <EmptyChart>No center data yet.</EmptyChart>
              )}
            </ChartCard>

            <Card p='24px' w='100%'>
              <Text color={textColor} fontSize='lg' fontWeight='700' mb='18px'>
                Recent Payments
              </Text>
              <Box overflowX='auto'>
                <Table size='sm'>
                  <Thead>
                    <Tr>
                      <Th>Student</Th>
                      <Th>Method</Th>
                      <Th isNumeric>Amount</Th>
                      <Th>Status</Th>
                      <Th>Date</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {recentPayments.length ? (
                      recentPayments.map((payment) => (
                        <Tr key={payment.id || payment._id || payment.reference_number} _hover={{ bg: rowHoverBg }}>
                          <Td color={textColor}>{payment.student_name || payment.reference_number || '-'}</Td>
                          <Td color={textColorSecondary}>{formatPaymentMethod(payment.payment_method)}</Td>
                          <Td color={textColor} isNumeric>{formatCurrency(payment.amount)}</Td>
                          <Td color={textColorSecondary}>{formatStatus(payment.status)}</Td>
                          <Td color={textColorSecondary}>{formatDate(payment.payment_date || payment.created_at)}</Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={5} textAlign='center' py='24px' color={textColorSecondary}>
                          No recent payments found.
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            </Card>
          </SimpleGrid>

          <Card p='24px' w='100%'>
            <Text color={textColor} fontSize='lg' fontWeight='700' mb='18px'>
              Recent Student Applications
            </Text>
            <Box overflowX='auto'>
              <Table size='sm'>
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Matricule</Th>
                    <Th>Center</Th>
                    <Th>Field</Th>
                    <Th>Date</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {recentStudents.length ? (
                    recentStudents.map((student) => (
                      <Tr key={student.id || student._id || student.matricule} _hover={{ bg: rowHoverBg }}>
                        <Td color={textColor}>{student.name || '-'}</Td>
                        <Td color={textColorSecondary}>{student.matricule || '-'}</Td>
                        <Td color={textColorSecondary}>{student.center || '-'}</Td>
                        <Td color={textColorSecondary}>{student.field || '-'}</Td>
                        <Td color={textColorSecondary}>{formatDate(student.application_date || student.created_at)}</Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan={5} textAlign='center' py='24px' color={textColorSecondary}>
                        No recent applications found.
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          </Card>
        </VStack>
      )}

      <SimpleGrid columns={{ base: 1, xl: 2 }} gap='20px' alignItems='start'>
        <Card p='24px' bg='transparent' boxShadow='none' w='100%'>
          <Text color={primaryColor} fontSize='xl' fontWeight='700' mb='8px'>
            Data Table
          </Text>
          <Text color={textColorSecondary} fontSize='sm' mb='20px'>
            Build on-screen lists in the browser. Filters match the Export column on the right — same order,
            stacked top to bottom.
          </Text>
          <VStack spacing='24px' align='stretch' w='100%'>
            <Box
              borderWidth='1px'
              borderColor={panelBorder}
              borderRadius='12px'
              bg={panelBg}
              p={{ base: '16px', md: '18px' }}
              w='100%'
            >
              <Text color={textColor} fontSize='sm' fontWeight='700' mb='12px'>
                Class list
              </Text>
              <SimpleGrid columns={1} gap='14px'>
                <StudentSharedFilters
                  studentFilters={studentFilters}
                  onStudentChange={handleStudentFilterChange}
                  loadingCenters={loadingCenters}
                  centers={centers}
                  isCenterScoped={isCenterScoped}
                  centerName={centerName}
                  inputBg={inputBg}
                  textColor={textColor}
                />

                <SimpleGrid columns={{ base: 1, md: 2 }} gap='12px' w='100%'>
                  <FormControl>
                    <FormLabel color={textColor} fontSize='sm' mb='4px'>
                      Skip{' '}
                      <Text as='span' fontWeight='normal' color={textColorSecondary}>
                        (table)
                      </Text>
                    </FormLabel>
                    <Input
                      type='number'
                      min={0}
                      value={studentFilters.skip}
                      onChange={(e) => handleStudentFilterChange('skip', e.target.value)}
                      bg={inputBg}
                      color={textColor}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel color={textColor} fontSize='sm' mb='4px'>
                      Limit{' '}
                      <Text as='span' fontWeight='normal' color={textColorSecondary}>
                        (table, max 1000)
                      </Text>
                    </FormLabel>
                    <Input
                      type='number'
                      min={1}
                      max={1000}
                      value={studentFilters.limit}
                      onChange={(e) => handleStudentFilterChange('limit', e.target.value)}
                      bg={inputBg}
                      color={textColor}
                    />
                  </FormControl>
                </SimpleGrid>

                <Box display='flex' justifyContent='flex-end'>
                  <Button colorScheme='brand' size='sm' onClick={handleGenerateList}>
                    Generate list
                  </Button>
                </Box>
              </SimpleGrid>
            </Box>

            <Divider borderColor={panelBorder} />

            <Box
              borderWidth='1px'
              borderColor={panelBorder}
              borderRadius='12px'
              bg={panelBg}
              p={{ base: '16px', md: '18px' }}
              w='100%'
            >
              <Text color={textColor} fontSize='sm' fontWeight='700' mb='12px'>
                Tuition payments
              </Text>
              <SimpleGrid columns={1} gap='14px'>
                <TuitionSharedFilters
                  tuitionFilters={tuitionFilters}
                  onTuitionChange={handleTuitionFilterChange}
                  loadingCenters={loadingCenters}
                  centers={centers}
                  isCenterScoped={isCenterScoped}
                  centerName={centerName}
                  inputBg={inputBg}
                  textColor={textColor}
                />

                <SimpleGrid columns={{ base: 1, md: 2 }} gap='12px' w='100%'>
                  <FormControl>
                    <FormLabel color={textColor} fontSize='sm' mb='4px'>
                      Skip{' '}
                      <Text as='span' fontWeight='normal' color={textColorSecondary}>
                        (table)
                      </Text>
                    </FormLabel>
                    <Input
                      type='number'
                      min={0}
                      value={tuitionFilters.skip}
                      onChange={(e) => handleTuitionFilterChange('skip', e.target.value)}
                      bg={inputBg}
                      color={textColor}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel color={textColor} fontSize='sm' mb='4px'>
                      Limit{' '}
                      <Text as='span' fontWeight='normal' color={textColorSecondary}>
                        (table, max 1000)
                      </Text>
                    </FormLabel>
                    <Input
                      type='number'
                      min={1}
                      max={1000}
                      value={tuitionFilters.limit}
                      onChange={(e) => handleTuitionFilterChange('limit', e.target.value)}
                      bg={inputBg}
                      color={textColor}
                    />
                  </FormControl>
                </SimpleGrid>

                <Box display='flex' justifyContent='flex-end'>
                  <Button colorScheme='brand' size='sm' onClick={handleGenerateTuitionList}>
                    Generate list
                  </Button>
                </Box>
              </SimpleGrid>
            </Box>
          </VStack>
        </Card>

        <Card p='24px' bg='transparent' boxShadow='none' w='100%'>
          <Text color={primaryColor} fontSize='xl' fontWeight='700' mb='8px'>
            Exports
          </Text>
          <Text color={textColorSecondary} fontSize='sm' mb='20px'>
            Same filters as the Data Table column on the left. Table “limit” is only for on-screen lists;
            set PDF row cap here (1–10000, default 1000).
          </Text>
          <VStack spacing='24px' align='stretch' w='100%'>
            <Box
              borderWidth='1px'
              borderColor={panelBorder}
              borderRadius='12px'
              bg={panelBg}
              p={{ base: '16px', md: '18px' }}
              w='100%'
            >
              <Text color={textColor} fontSize='sm' fontWeight='700' mb='12px'>
                Class list PDF
              </Text>
              <SimpleGrid columns={1} gap='14px' w='100%'>
                <StudentSharedFilters
                  studentFilters={studentFilters}
                  onStudentChange={handleStudentFilterChange}
                  loadingCenters={loadingCenters}
                  centers={centers}
                  isCenterScoped={isCenterScoped}
                  centerName={centerName}
                  inputBg={inputBg}
                  textColor={textColor}
                />
                <SimpleGrid columns={{ base: 1, md: 2 }} gap='12px' w='100%'>
                  <FormControl>
                    <FormLabel color={textColor} fontSize='sm' mb='4px'>
                      Skip <Text as='span' fontWeight='normal' color={textColorSecondary}>(export)</Text>
                    </FormLabel>
                    <Input
                      type='number'
                      min={0}
                      value={studentFilters.skip}
                      onChange={(e) => handleStudentFilterChange('skip', e.target.value)}
                      bg={inputBg}
                      color={textColor}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel color={textColor} fontSize='sm' mb='4px'>
                      PDF rows (limit)
                    </FormLabel>
                    <Input
                      type='number'
                      min={1}
                      max={10000}
                      value={classListPdfLimit}
                      onChange={(e) => handleClassListPdfLimitChange(e.target.value)}
                      bg={inputBg}
                      color={textColor}
                    />
                  </FormControl>
                </SimpleGrid>
                <Box display='flex' justifyContent='flex-end'>
                  <Button
                    colorScheme='brand'
                    size='sm'
                    isLoading={classListPdfDownloading}
                    loadingText='Downloading'
                    onClick={handleDownloadClassListPdf}
                  >
                    Download class list PDF
                  </Button>
                </Box>
              </SimpleGrid>
            </Box>

            <Divider borderColor={panelBorder} />

            <Box
              borderWidth='1px'
              borderColor={panelBorder}
              borderRadius='12px'
              bg={panelBg}
              p={{ base: '16px', md: '18px' }}
              w='100%'
            >
              <Text color={textColor} fontSize='sm' fontWeight='700' mb='12px'>
                Tuition payments PDF
              </Text>
              <SimpleGrid columns={1} gap='14px' w='100%'>
                <TuitionSharedFilters
                  tuitionFilters={tuitionFilters}
                  onTuitionChange={handleTuitionFilterChange}
                  loadingCenters={loadingCenters}
                  centers={centers}
                  isCenterScoped={isCenterScoped}
                  centerName={centerName}
                  inputBg={inputBg}
                  textColor={textColor}
                />
                <SimpleGrid columns={{ base: 1, md: 2 }} gap='12px' w='100%'>
                  <FormControl>
                    <FormLabel color={textColor} fontSize='sm' mb='4px'>
                      Skip <Text as='span' fontWeight='normal' color={textColorSecondary}>(export)</Text>
                    </FormLabel>
                    <Input
                      type='number'
                      min={0}
                      value={tuitionFilters.skip}
                      onChange={(e) => handleTuitionFilterChange('skip', e.target.value)}
                      bg={inputBg}
                      color={textColor}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel color={textColor} fontSize='sm' mb='4px'>
                      PDF rows (limit)
                    </FormLabel>
                    <Input
                      type='number'
                      min={1}
                      max={10000}
                      value={tuitionPdfLimit}
                      onChange={(e) => handleTuitionPdfLimitChange(e.target.value)}
                      bg={inputBg}
                      color={textColor}
                    />
                  </FormControl>
                </SimpleGrid>
                <Box display='flex' justifyContent='flex-end'>
                  <Button
                    colorScheme='brand'
                    size='sm'
                    isLoading={tuitionPdfDownloading}
                    loadingText='Downloading'
                    onClick={handleDownloadTuitionPdf}
                  >
                    Download tuition PDF
                  </Button>
                </Box>
              </SimpleGrid>
            </Box>
          </VStack>
        </Card>
      </SimpleGrid>
    </Box>
  );
}
